import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/i18n';
import type { Outcome, Pillar, ProgrammeActivity, Scene, WeekSummary } from '@/api/types';
import { Button, Illustration, RoomTag, STICKERS, StickerIcon, StickerSlot, type StickerKind } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { exerciseImage, sceneImage } from '@/lib/illustrations';
import { ROOM_STYLE, type RoomKey } from '@/lib/rooms';
import { SceneIcon } from '@/features/report/programme';
import { doneToday, type ProgrammeState } from './useProgramme';

/** "Follow the Point: Gaze Shift to Pointed Object" (older programmes use a dash) → ["Follow the Point", "Gaze Shift to Pointed Object"]. */
export const splitTitle = (title: string): [string, string | undefined] => {
  const [head, ...rest] = title.split(/\s+[—–-]\s+|:\s+/);
  return [head ?? title, rest.length ? rest.join(', ') : undefined];
};

/**
 * The activity's own illustration; until one exists, the picture of the room it happens in,
 * then the drawn scene card the PDF uses.
 */
export const ActivityPicture = ({
  exerciseId,
  pillar,
  scene,
  room,
  className = '',
}: {
  exerciseId: string;
  pillar: Pillar;
  scene?: Scene;
  room?: RoomKey;
  className?: string;
}) => {
  const src = exerciseImage(exerciseId) ?? (room ? sceneImage(ROOM_STYLE[room].scene) : undefined);
  if (src) return <Illustration src={src} alt="" className={className} position="50% 45%" />;
  return (
    <div className={cx('flex items-center justify-center bg-paper', className)}>
      <SceneIcon scene={scene ?? 'play'} pillar={pillar} className="h-full max-h-[160px] w-auto" />
    </div>
  );
};

export const DoneDots = ({ count, of = 3 }: { count: number; of?: number }) => (
  <span aria-hidden="true" className="inline-flex gap-1">
    {Array.from({ length: of }, (_, i) => (
      <span key={i} className={cx('h-2.5 w-2.5 rounded-full', i < count ? 'bg-coral-500' : 'bg-line')} />
    ))}
  </span>
);

export const ActivityCard = ({
  activity,
  scene,
  count,
  isPick,
  to,
}: {
  activity: ProgrammeActivity;
  scene?: Scene;
  count: number;
  isPick: boolean;
  to: string;
}) => {
  const t = useT();
  const [title, sub] = splitTitle(activity.title);
  return (
    <Link
      to={to}
      className={cx(
        'lift group flex w-full flex-col overflow-hidden rounded-lg bg-white shadow-1 focus-visible:outline-offset-2',
        isPick && 'ring-[3px] ring-coral-500',
      )}
    >
      <div className="relative h-[150px]">
        <ActivityPicture exerciseId={activity.exerciseId} pillar={activity.pillar} scene={scene} room={activity.room} className="h-full w-full" />
        <RoomTag room={activity.room} className="absolute left-2.5 top-2.5" />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-4">
        <h3 className="text-[19px] leading-tight">{title}</h3>
        {sub && <p className="text-caption text-ink-muted">{sub}</p>}
        <p className="text-caption text-ink-muted">{t('act.minutes', { n: activity.durationMinutes })}</p>
        <p className="mt-auto flex items-center justify-between pt-2 text-caption text-ink-muted">
          <span className={isPick ? 'font-extrabold text-coral-600' : undefined}>
            {isPick ? t('prog.todaysPick') : count > 0 ? t('prog.doneWeek', { n: Math.min(count, 3) }) : t('prog.notStarted')}
          </span>
          <DoneDots count={count} />
        </p>
      </div>
    </Link>
  );
};

/** Seven days, Monday first; a sticker on each day the family tried something. */
export const StickerWeek = ({ week, title, hint }: { week: WeekSummary; title: string; hint?: string }) => {
  const t = useT();
  const short = t('day.short').split(',');
  const long = t('day.long').split(',');
  return (
    <section className="rounded-lg bg-white p-5 shadow-1" aria-label={title}>
      <h2 className="text-[20px]">{title}</h2>
      {hint && <p className="mt-1 text-caption text-ink-muted">{hint}</p>}
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {week.days.map((d, i) => {
          const kind = d.stickers[0];
          return (
            <StickerSlot
              key={d.date}
              kind={kind}
              today={d.date === week.today}
              label={kind ? t('sticker.done', { day: long[i] ?? d.date, sticker: t(`sticker.${kind}`) }) : t('sticker.empty', { day: long[i] ?? d.date })}
            />
          );
        })}
      </div>
      <div aria-hidden="true" className="mt-1 grid grid-cols-7 gap-1.5 text-center text-[11.5px] font-extrabold text-ink-muted">
        {short.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
    </section>
  );
};

const OUTCOME_KEYS: Array<[Outcome, string, string]> = [
  ['enjoyed', 'act.enjoyed', '😊'],
  ['tried', 'act.tried', '🙂'],
  ['not_today', 'act.notToday', '😴'],
];

/**
 * "How did it go today?" → a sticker for the child to pick. Logs one entry per activity per day;
 * choosing again replaces it and Undo removes it.
 */
export const LogBar = ({
  exerciseId,
  childName,
  state,
  compact = false,
}: {
  exerciseId: string;
  childName: string;
  state: ProgrammeState;
  compact?: boolean;
}) => {
  const t = useT();
  const week = state.data?.week;
  const today = week ? doneToday(week, exerciseId) : undefined;
  const [pending, setPending] = useState<Outcome>();

  const choose = async (outcome: Outcome) => {
    if (outcome === 'not_today') {
      setPending(undefined);
      await state.log(exerciseId, outcome);
      return;
    }
    setPending(outcome);
  };
  const pick = async (sticker: StickerKind) => {
    if (!pending) return;
    if (await state.log(exerciseId, pending, sticker)) setPending(undefined);
  };

  if (!week) return null;

  if (pending) {
    return (
      <div className="rounded-lg bg-coral-100 p-5 text-center" aria-live="polite">
        <p className="font-display text-[22px] font-bold text-ink">{t('act.pickSticker', { child: childName })}</p>
        <div className="mt-3 flex justify-center gap-3">
          {STICKERS.map((k) => (
            <button
              key={k}
              type="button"
              disabled={state.saving}
              onClick={() => pick(k)}
              aria-label={t(`sticker.${k}`)}
              className="flex h-16 w-16 items-center justify-center rounded-[18px] bg-white shadow-1 transition duration-snappy ease-snappy hover:scale-105 disabled:opacity-60"
            >
              <StickerIcon kind={k} size={34} />
            </button>
          ))}
        </div>
        {state.saveError && <p className="mt-3 text-caption text-alert-ink">{t('act.saveFailed')}</p>}
      </div>
    );
  }

  return (
    <div className={cx('rounded-lg bg-white shadow-1', compact ? 'p-4' : 'p-5')} aria-live="polite">
      <div className="flex flex-wrap items-center gap-3">
        <p className="mr-auto font-display text-[19px] font-bold">
          {today ? t('act.saved') : t('act.howWent')}
        </p>
        {today?.sticker && <StickerIcon kind={today.sticker} size={26} />}
        {OUTCOME_KEYS.map(([outcome, key, face]) => (
          <button
            key={outcome}
            type="button"
            disabled={state.saving}
            aria-pressed={today?.outcome === outcome}
            onClick={() => choose(outcome)}
            className={cx(
              'inline-flex min-h-btn items-center gap-2 rounded-md border-2 px-4 text-label font-extrabold transition-colors disabled:opacity-60',
              today?.outcome === outcome ? 'border-coral-500 bg-coral-100' : 'border-line bg-white hover:border-sage-300',
            )}
          >
            <span aria-hidden="true">{face}</span>
            {t(key)}
          </button>
        ))}
        {today && (
          <Button variant="tertiary" onClick={() => state.unlog(exerciseId)} disabled={state.saving}>
            {t('act.undo')}
          </Button>
        )}
      </div>
      {state.saveError && <p className="mt-3 text-caption text-alert-ink">{t('act.saveFailed')}</p>}
    </div>
  );
};
